import type { TowerType } from '../core/types';
import { COLA_EXCLUDED, EFFECT_DURATION_MS, EFFECT_ICON, type EffectKind } from './effects';
import { fnv1a, mulberry32 } from './prng';

/**
 * The Potion Shop (spec: docs/superpowers/specs/2026-09-02-the-hub-design.md
 * §4; six potions and the shared shelf since Revision 9). Pure: the clock is
 * passed in, and the stock roll uses a seeded PRNG, so no ambient randomness
 * enters src/config.
 */

/**
 * The shop's kinds. The Diamond, Space Debris and the Moon Stone are timed
 * effects too, but they are level PRIZES — none is ever for sale, so none of
 * them is a potion.
 */
export type PotionKind = Exclude<
  EffectKind,
  | 'diamond'
  | 'debris'
  | 'moonstone'
  | 'goldstone'
  | 'capystone'
  | 'darkshard'
  | 'lightshard'
  | 'shroom'
  | 'snowshroom'
>;

export const POTION_ORDER: readonly PotionKind[] = [
  'pickle',
  'chili',
  'cola',
  'burger',
  'samurai',
  'oliveoil',
];

export interface PotionDef {
  kind: PotionKind;
  name: string;
  cost: number;
  durationMs: number;
  /** One line for the shop panel. */
  effect: string;
  /** Needs a tower picked on Use. */
  targeted: boolean;
  /** Towers the picker greys out. */
  excludes: readonly TowerType[];
  /** Shop art (96px). */
  art: string;
  /** Timer / tower icon (64px). */
  icon: string;
  /** Chance (%) of filling any one slot. The six sum to 100. */
  odds: number;
}

export const POTIONS: Record<PotionKind, PotionDef> = {
  pickle: {
    kind: 'pickle',
    name: 'Pickle Jar',
    cost: 150,
    durationMs: EFFECT_DURATION_MS.pickle,
    effect: 'One tower deals 25% more damage for 3:00',
    targeted: true,
    excludes: [],
    art: 'potion-pickle',
    icon: EFFECT_ICON.pickle,
    odds: 30,
  },
  chili: {
    kind: 'chili',
    name: 'Chili',
    cost: 200,
    durationMs: EFFECT_DURATION_MS.chili,
    effect: 'One tower slows enemies in range 10% more for 1:30',
    targeted: true,
    excludes: [],
    art: 'potion-chili',
    icon: EFFECT_ICON.chili,
    odds: 25,
  },
  cola: {
    kind: 'cola',
    name: 'COLA',
    cost: 250,
    durationMs: EFFECT_DURATION_MS.cola,
    effect: 'One tower fires 25% faster for 2:00 (not Ninja Hambi)',
    targeted: true,
    excludes: COLA_EXCLUDED,
    art: 'potion-cola',
    icon: EFFECT_ICON.cola,
    odds: 20,
  },
  burger: {
    kind: 'burger',
    name: 'Bottleburger',
    cost: 300,
    durationMs: EFFECT_DURATION_MS.burger,
    effect: 'Every Hambi deals 25% more damage for 1:30',
    targeted: false,
    excludes: [],
    art: 'potion-burger',
    icon: EFFECT_ICON.burger,
    odds: 15,
  },
  samurai: {
    kind: 'samurai',
    name: "Samurai's Taste",
    cost: 350,
    durationMs: EFFECT_DURATION_MS.samurai,
    effect: 'Every melee Hambi deals 35% more damage for 2:00',
    targeted: false,
    excludes: [],
    art: 'potion-samurai',
    icon: EFFECT_ICON.samurai,
    odds: 7,
  },
  oliveoil: {
    kind: 'oliveoil',
    name: 'Olive Oil',
    cost: 400,
    durationMs: EFFECT_DURATION_MS.oliveoil,
    effect: 'Every Hambi is immune to all damage for 1:00',
    targeted: false,
    excludes: [],
    art: 'potion-oliveoil',
    icon: EFFECT_ICON.oliveoil,
    odds: 3,
  },
};

export function isPotionKind(v: unknown): v is PotionKind {
  return typeof v === 'string' && (POTION_ORDER as readonly string[]).includes(v);
}

/**
 * The names `/give <target> potion <name>` accepts, mapped to kinds. The
 * burger's command name is "bottleburger" (one word). Must match `dev_give`.
 */
export const POTION_COMMAND_NAMES: Record<string, PotionKind> = {
  pickle: 'pickle',
  chili: 'chili',
  cola: 'cola',
  bottleburger: 'burger',
  samurai: 'samurai',
  oliveoil: 'oliveoil',
};

export function potionFromCommandName(v: unknown): PotionKind | null {
  return typeof v === 'string' ? (POTION_COMMAND_NAMES[v.toLowerCase()] ?? null) : null;
}

/** Slots on the Potions tab. */
export const POTION_SLOTS = 3;

/** Stock rolls over every ten minutes, aligned to the wall clock. */
export const RESTOCK_MS = 10 * 60_000;

/**
 * Which ten-minute cycle `now` (epoch ms) falls in, measured from `epoch`
 * (also epoch ms; 0 = the wall clock itself). `/restock potionshop` moves the
 * epoch to "now" for everyone (see account/potionRestock), which restarts
 * the timer at a full ten minutes and, because the cycle number changes,
 * rerolls the stock and clears the SOLD flags. Cycles before the epoch count
 * down into negatives, which is harmless — only equality and order matter.
 */
export function cycleIndex(now: number, epoch = 0): number {
  return Math.floor((now - epoch) / RESTOCK_MS);
}

/** Epoch ms at which the current cycle's stock is replaced. */
export function cycleEndsAt(now: number, epoch = 0): number {
  return epoch + (cycleIndex(now, epoch) + 1) * RESTOCK_MS;
}

/**
 * One tick of the restock watcher: given the last cycle seen and the clock,
 * the cycle now and whether it rolled over (→ the "restocked" banner).
 */
export function restockStep(
  lastCycle: number,
  now: number,
  epoch = 0,
): { cycle: number; rolled: boolean } {
  const cycle = cycleIndex(now, epoch);
  return { cycle, rolled: cycle !== lastCycle };
}

/** Pick a potion from a [0,1) roll by the published odds. */
export function potionForRoll(u: number): PotionKind {
  let acc = 0;
  for (const kind of POTION_ORDER) {
    acc += POTIONS[kind].odds / 100;
    if (u < acc) return kind;
  }
  return POTION_ORDER[POTION_ORDER.length - 1];
}

/**
 * The three slots for a cycle. Deterministic in (epoch, cycle) and NOTHING
 * else: every player sees the same shelf (Revision 9). Reloading the page in
 * the same cycle shows the same stock; SOLD flags stay per player.
 */
export function rollStock(cycle: number, epoch = 0): PotionKind[] {
  // The epoch is part of the seed, so a restock never repeats an old roll
  // even when the relative cycle number happens to coincide.
  const rand = mulberry32(fnv1a(`${epoch}:${cycle}`));
  const out: PotionKind[] = [];
  for (let i = 0; i < POTION_SLOTS; i++) out.push(potionForRoll(rand()));
  return out;
}

export interface PotionShopState {
  cycle: number;
  sold: boolean[];
}

/** Parse `hambi.potionShop`; anything malformed reads as "nothing sold". */
export function parsePotionShop(raw: string | null): PotionShopState | null {
  if (!raw) return null;
  try {
    const p = JSON.parse(raw) as unknown;
    if (!p || typeof p !== 'object') return null;
    const r = p as Record<string, unknown>;
    const soldRaw: unknown = r.sold;
    if (typeof r.cycle !== 'number' || !Array.isArray(soldRaw)) return null;
    const sold = Array.from({ length: POTION_SLOTS }, (_, i) => soldRaw[i] === true);
    return { cycle: r.cycle, sold };
  } catch {
    return null;
  }
}

/** The SOLD flags that apply to `cycle` — a stale record means fresh stock. */
export function soldFor(state: PotionShopState | null, cycle: number): boolean[] {
  if (!state || state.cycle !== cycle) return Array.from({ length: POTION_SLOTS }, () => false);
  return state.sold.slice(0, POTION_SLOTS);
}

/** Parse `hambi.potions` (the stored, unused potions). */
export function parsePotions(raw: string | null): PotionKind[] {
  if (!raw) return [];
  try {
    const p = JSON.parse(raw) as unknown;
    return Array.isArray(p) ? p.filter(isPotionKind) : [];
  } catch {
    return [];
  }
}
